import React, { useRef, useState } from 'react';
import { Button } from './ui/Button';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface SubscriptionButtonProps {
  priceId?: string;
  productId?: string;
  planName: string;
  amount: string;
  onSuccess?: () => void;
  className?: string;
  trialDays?: number; // Optional: add trial period in days
  label?: string; // Optional: custom button label
  billingInterval?: 'month' | 'year';
}

export const SubscriptionButton: React.FC<SubscriptionButtonProps> = ({
  priceId,
  productId,
  planName,
  amount,
  onSuccess,
  className,
  trialDays,
  label,
  billingInterval
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const inFlightRef = useRef(false);

  const handleSubscribe = async () => {
    if (isLoading || inFlightRef.current) {
      return;
    }

    if (!user) {
      toast.error('Veuillez vous connecter pour continuer');
      return;
    }

    inFlightRef.current = true;
    setIsLoading(true);

    let loaderToastId: string | undefined;

    try {
      if (!priceId && !productId) {
        throw new Error('Identifiant du plan Stripe manquant');
      }

      if (!supabase) {
        throw new Error('Supabase non configuré');
      }
      
      // Obtenir et rafraîchir la session si nécessaire
      let { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      // Si pas de session ou session expirée, essayer de rafraîchir
      if (!session || sessionError) {
        const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshedSession) {
          session = refreshedSession;
          sessionError = null;
        }
      }
      
      if (sessionError || !session?.access_token) {
        // Si toujours pas de session valide, demander à l'utilisateur de se reconnecter
        toast.error('Votre session a expiré. Veuillez vous reconnecter.');
        window.location.href = '/auth/signin';
        return;
      }

      loaderToastId = toast.loading('Création de la session de paiement...');

      // Appeler l'Edge Function Supabase
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(priceId ? { price_id: priceId } : {}),
          ...(productId ? { product_id: productId } : {}),
          plan_name: planName,
          billing_interval: billingInterval,
          success_url: `${window.location.origin}/dashboard?success=true`,
          cancel_url: `${window.location.origin}/upgrade?canceled=true`,
          mode: 'subscription',
          ...(trialDays && trialDays > 0 ? { trial_period_days: trialDays } : {}),
        }),
      });

      let payload: any = null;
      try {
        payload = await response.clone().json();
      } catch {
        // ignore JSON parsing failure - handled below
      }

      if (!response.ok) {
        const serverMessage = typeof payload?.error === 'string' ? payload.error : null;
        const friendlyMessage =
          serverMessage ??
          (response.status === 401
            ? 'Votre session a expiré. Veuillez vous reconnecter.'
            : response.status === 402
              ? 'Le mode de paiement a été refusé par Stripe.'
              : `Erreur lors de la création de la session de paiement (code ${response.status}).`);
        throw new Error(friendlyMessage);
      }

      const redirectUrl = typeof payload?.url === 'string' ? payload.url : null;
      if (!redirectUrl) {
        throw new Error('URL de redirection Stripe indisponible.');
      }

      if (loaderToastId) {
        toast.success('Redirection vers Stripe...', { id: loaderToastId });
      } else {
        toast.success('Redirection vers Stripe...');
      }
      onSuccess?.();
      window.location.assign(redirectUrl);

    } catch (error) {
      console.error('Erreur de souscription:', error);
      const message = error instanceof Error ? error.message : 'Échec du processus de souscription';
      if (loaderToastId) {
        toast.error(message, { id: loaderToastId });
      } else {
        toast.error(message);
      }
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handleSubscribe}
      disabled={isLoading}
      className={className}
      aria-busy={isLoading}
    >
      {isLoading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin text-white" />
          <span className="text-white">Traitement...</span>
        </>
      ) : (
        <span className="text-white">{label ? label : `S'abonner à ${planName} - ${amount}`}</span>
      )}
    </Button>
  );
};

export default SubscriptionButton;
